import React, { useEffect, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { FaTimes } from "react-icons/fa";
import { BsChevronDown, BsChevronRight, BsSearch } from "react-icons/bs";

import { useNavigationContext } from "../../../context/navigation/navigationContext";
import { useAdminContext } from "../../../context/admin/context/AdminContext";
import { clearBreadCumb, resetBreadCumb } from "../../../middlewares/breadCrumb/BreadCrumb";
import { clearRoutingHistory } from "../../../middlewares";
import { MenuCategories } from "./menuConfig";

// --- Mobile Portal ---
const MobileLayer = ({ children }) => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  return mounted ? createPortal(children, document.body) : null;
};

// --- Sidebar Content UI ---
const SidebarContent = ({ onClose }) => {
  const { sysAdmin, selectedMenu, setSelectedMenu, setSingleAdmin } = useAdminContext();
  const [query, setQuery] = useState("");
  const location = useLocation();
  const userRole = sysAdmin?.role;

  // Smart Search: Filters Categories and Items
  const filteredCategories = useMemo(() => {
    const q = query.trim().toLowerCase();

    return MenuCategories.map(category => {
      const filteredItems = category.items.filter(item => {
        // 1. Check permissions
        if (item.roles && !item.roles.includes(userRole)) return false;
        // 2. If no search query, show item
        if (!q) return true;
        // 3. Search logic
        const titleMatch = item.title.toLowerCase().includes(q);
        const childMatch = item.menu?.some(m => m.title.toLowerCase().includes(q));
        return titleMatch || childMatch;
      });
      // Return category with its filtered items
      return { ...category, items: filteredItems };
    }).filter(category => category.items.length > 0); // Hide empty categories
  }, [query, userRole]);

  const handleLinkClick = () => {
    clearBreadCumb();
    resetBreadCumb();
    setSingleAdmin(null);
    clearRoutingHistory();
    onClose();
  };

  return (
    <div className="flex h-full flex-col bg-[#F9FAFB]"> {/* Very light gray background for the whole sidebar */}

      {/* Brand Header */}
      <div className="flex shrink-0 items-center justify-between border-b border-gray-200/60 bg-white px-5 py-4">
        <div className="flex items-center gap-3">
          <span aria-hidden className="inline-block h-2.5 w-2.5 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]" />
          <div>
            <h1 className="text-[14px] font-bold tracking-tight text-gray-900">DóseeAfrica</h1>
            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-widest">Admin Portal</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-900 xl:hidden"
        >
          <FaTimes size={14} />
        </button>
      </div>

      {/* Scrollable Nav */}
      <div className="custom-scroll flex-1 overflow-y-auto px-4 py-5" style={{ scrollbarGutter: "stable" }}>

        {/* Search */}
        <div className="relative mb-6">
          <BsSearch className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={query}
            placeholder="Search menu…"
            onChange={(e) => setQuery(e.target.value)}
            className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-[13px] text-gray-900 shadow-sm transition-colors placeholder:text-gray-400 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
          />
        </div>

        {/* Categories Rendering */}
        <nav className="flex flex-col gap-6">
          {filteredCategories.map((category, catIdx) => (
            <div key={catIdx}>
              {/* Category Header */}
              <h3 className="mb-2 px-2 text-[10px] font-bold uppercase tracking-wider text-gray-400">
                {category.label}
              </h3>

              <ul className="flex flex-col gap-0.5">
                {category.items.map((item, itemIdx) => {
                  const uniqueId = `${catIdx}-${itemIdx}`;
                  const isOpen = selectedMenu === uniqueId;

                  return (
                    <li key={uniqueId}>
                      {/* Main Accordion Button (Flat, no borders) */}
                      <button
                        onClick={() => setSelectedMenu(isOpen ? null : uniqueId)}
                        className={`group flex w-full items-center justify-between rounded-lg px-2.5 py-2 transition-colors ${isOpen
                            ? "bg-gray-900 text-white shadow-md"
                            : "text-gray-600 hover:bg-white hover:text-gray-900"
                          }`}
                      >
                        <div className="flex items-center gap-3">
                          <span className={`text-[16px] ${isOpen ? "text-emerald-400" : "text-gray-400 group-hover:text-gray-700"}`}>
                            {item.icon}
                          </span>
                          <span className="text-[13px] font-medium">{item.title}</span>
                        </div>
                        <div className="opacity-70">
                          {isOpen ? <BsChevronDown size={12} /> : <BsChevronRight size={12} />}
                        </div>
                      </button>

                      {/* Sub-menu (Grid Animation) */}
                      <div
                        className={`grid transition-[grid-template-rows] duration-200 ease-in-out ${isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                          }`}
                      >
                        <div className="overflow-hidden">
                          {/* Visual threading line */}
                          <ul className="ml-[17px] mt-1 flex flex-col gap-1 border-l border-gray-200 py-1 pl-3">
                            {item.menu?.map((subItem, subIdx) => {
                              if (subItem.roles && !subItem.roles.includes(userRole)) return null;

                              const isActive = location.pathname === subItem.ref;

                              return (
                                <li key={subIdx}>
                                  <Link
                                    to={subItem.ref}
                                    onClick={handleLinkClick}
                                    className={`block rounded-md px-3 py-1.5 text-[12px] transition-colors ${isActive
                                        ? "bg-emerald-50 font-semibold text-emerald-700"
                                        : "font-medium text-gray-500 hover:text-gray-900"
                                      }`}
                                  >
                                    {subItem.title}
                                  </Link>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>
      </div>

      {/* Internal Custom CSS for elegant scrollbar */}
      <style>{`
        .custom-scroll::-webkit-scrollbar { width: 4px; }
        .custom-scroll::-webkit-scrollbar-track { background: transparent; }
        .custom-scroll::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 20px; }
        .custom-scroll:hover::-webkit-scrollbar-thumb { background-color: #94a3b8; }
      `}</style>
    </div>
  );
};

// --- Wrapper ---
const SideBar = () => {
  const { showSideBar, setShowSideBar } = useNavigationContext();

  useEffect(() => {
    if (!showSideBar || window.innerWidth >= 1280) return;
    const originalStyle = document.documentElement.style.overflow;
    document.documentElement.style.overflow = "hidden";
    return () => { document.documentElement.style.overflow = originalStyle; };
  }, [showSideBar]);

  return (
    <>
      <aside className="hidden w-[280px] shrink-0 flex-col border-r border-gray-200 xl:flex xl:h-screen">
        <SidebarContent onClose={() => setShowSideBar(false)} />
      </aside>

      <MobileLayer>
        <div
          onClick={() => setShowSideBar(false)}
          className={`fixed inset-0 z-[9998] bg-gray-900/40 backdrop-blur-sm transition-opacity duration-300 xl:hidden ${showSideBar ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
            }`}
          aria-hidden="true"
        />
        <aside
          role="dialog"
          aria-modal="true"
          className={`fixed inset-y-0 left-0 z-[9999] flex h-full w-[280px] flex-col shadow-2xl transition-transform duration-300 ease-in-out xl:hidden ${showSideBar ? "translate-x-0" : "-translate-x-full"
            }`}
        >
          <SidebarContent onClose={() => setShowSideBar(false)} />
        </aside>
      </MobileLayer>
    </>
  );
};

export default SideBar;